/**
 * NewDepartmentDialog — admin creates a new department under an existing parent.
 * Owner: Phase 7+ — V18 item A.
 */
import { useEffect, useState } from 'react';
import { Button } from '@edms/design-system';
import { useCreateDepartment, type Department } from '../../api/orgHooks';
import styles from '../AuthorWorkbench/NewDocumentForm.module.css';

export function NewDepartmentDialog({
  isOpen, onClose, onCreated, departments,
}: {
  isOpen: boolean;
  onClose: () => void;
  onCreated: (d: Department) => void;
  departments: Department[];
}) {
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [parentId, setParentId] = useState('');
  const [siteCode, setSiteCode] = useState('');
  const [costCenter, setCostCenter] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const m = useCreateDepartment();

  useEffect(() => {
    if (isOpen) {
      setCode(''); setName(''); setParentId(''); setSiteCode(''); setCostCenter(''); setErr(null);
    }
  }, [isOpen]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    if (!code.trim() || !name.trim()) { setErr('Code + name are required.'); return; }
    try {
      const d = await m.mutateAsync({
        code: code.trim().toUpperCase(),
        name: name.trim(),
        parentId: parentId || null,
        siteCode: siteCode.trim() || undefined,
        costCenter: costCenter.trim() || undefined,
      });
      onCreated(d);
      onClose();
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
  }

  if (!isOpen) return null;
  return (
    <div className={styles.backdrop} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className={styles.dialog} role="dialog" aria-modal="true">
        <header className={styles.header}>
          <h2 className={styles.title}>New department</h2>
        </header>
        <form className={styles.form} onSubmit={onSubmit}>
          {err && <div className={styles.errorBanner} role="alert">{err}</div>}
          <div className={styles.field}>
            <label className={styles.label}>Code<span className={styles.required}>*</span></label>
            <input className={styles.input} value={code} onChange={(e) => setCode(e.target.value)} placeholder="e.g. PROD-CAPSULE" />
          </div>
          <div className={styles.field}>
            <label className={styles.label}>Name<span className={styles.required}>*</span></label>
            <input className={styles.input} value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Production — Capsule Filling" />
          </div>
          <div className={styles.field}>
            <label className={styles.label}>Parent department</label>
            <select className={styles.select} value={parentId} onChange={(e) => setParentId(e.target.value)}>
              <option value="">— Top-level (no parent) —</option>
              {departments.filter((d) => d.isActive).map((d) => (
                <option key={d.departmentId} value={d.departmentId}>{d.name}</option>
              ))}
            </select>
          </div>
          <div className={styles.field}>
            <label className={styles.label}>Site code</label>
            <input className={styles.input} value={siteCode} onChange={(e) => setSiteCode(e.target.value)} placeholder="e.g. RIYADH-SITE-1" />
          </div>
          <div className={styles.field}>
            <label className={styles.label}>Cost centre</label>
            <input className={styles.input} value={costCenter} onChange={(e) => setCostCenter(e.target.value)} placeholder="e.g. CC-PROD-CAPSULE" />
          </div>
          <footer className={styles.footer}>
            <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
            <Button type="submit" variant="primary" isLoading={m.isPending}>Create</Button>
          </footer>
        </form>
      </div>
    </div>
  );
}
